import { useState, useCallback } from 'react';
import { AlertType } from '../components/AlertModal';

interface AlertOptions {
  message: string;
  type?: AlertType;
  title?: string;
}

/**
 * Custom hook for managing alert state
 * @returns An object with alert state and functions to show and hide alerts
 */
export const useAlert = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [alertProps, setAlertProps] = useState<AlertOptions>({
    message: '',
    type: 'info',
    title: undefined
  });

  /**
   * Show an alert with the specified options
   */
  const showAlert = useCallback((options: AlertOptions) => {
    setAlertProps(options);
    setIsVisible(true);
  }, []);

  /**
   * Hide the currently displayed alert
   */
  const hideAlert = useCallback(() => {
    setIsVisible(false);
  }, []);

  /**
   * Convenience method for showing an info alert
   */
  const showInfo = useCallback((message: string, title?: string) => {
    showAlert({ message, type: 'info', title });
  }, [showAlert]);

  /**
   * Convenience method for showing a success alert
   */
  const showSuccess = useCallback((message: string, title?: string) => {
    showAlert({ message, type: 'success', title });
  }, [showAlert]);

  /**
   * Convenience method for showing a warning alert
   */
  const showWarning = useCallback((message: string, title?: string) => {
    showAlert({ message, type: 'warning', title });
  }, [showAlert]);

  /**
   * Convenience method for showing an error alert
   */
  const showError = useCallback((message: string, title?: string) => {
    showAlert({ message, type: 'error', title });
  }, [showAlert]);

  return {
    isVisible,
    alertProps,
    showAlert,
    hideAlert,
    showInfo,
    showSuccess,
    showWarning,
    showError
  };
};

export default useAlert; 